/**
 * Scribe – état de l'enregistrement partagé entre les onglets d'AGORA (même origine, canal BroadcastChannel
 * `agora:scribe`). L'onglet qui enregistre (il tient le verrou `agora:scribe`) diffuse son état à chaque changement ;
 * les autres onglets l'affichent (bouton du dock rouge avec la durée, panneau avec le titre, la durée et l'état) et
 * peuvent lui demander d'arrêter l'enregistrement. Aucun enregistrement ne passe d'un onglet à l'autre : seul l'onglet
 * qui enregistre arrête, comme si l'on avait cliqué « Arrêter l'enregistrement » chez lui.
 *
 * Messages :
 *  - `etat` (onglet qui enregistre) : état résumé de l'enregistrement (phase, réunion, durée, pause, hors ligne) ;
 *  - `fin` (onglet qui enregistre) : l'enregistrement n'est plus dans cet onglet (arrêté et envoyé, onglet fermé) ;
 *  - `demande` (autre onglet) : l'onglet qui enregistre renvoie son état (onglet ouvert pendant l'enregistrement) ;
 *  - `arreter` (autre onglet) : l'onglet qui enregistre arrête la réunion `reunion` si c'est bien la sienne.
 * Navigateur sans BroadcastChannel : les autres onglets gardent « Scribe enregistre dans un autre onglet » (verrou
 * vérifié toutes les 3 s).
 */
import { ETAT_INITIAL, type EtatScribe, type PhaseScribe } from './enregistreur';

/** Canal entre les onglets d'AGORA. */
export const CANAL_SCRIBE = 'agora:scribe';

/** Ce que l'onglet qui enregistre diffuse de son état (sans ses avis, propres à l'onglet). */
export type EtatOnglet = Pick<EtatScribe, 'phase' | 'reunion' | 'dureeMs' | 'depuis' | 'pause' | 'pauseDepuis' | 'enAttente' | 'horsLigne'>;

export type MessageOnglets =
  | { type: 'etat'; etat: EtatOnglet }
  | { type: 'fin' }
  | { type: 'demande' }
  | { type: 'arreter'; reunion: string };

/** État résumé à diffuser. */
export function resumerEtat(e: EtatScribe): EtatOnglet {
  return {
    phase: e.phase, reunion: e.reunion ? { id: e.reunion.id, titre: e.reunion.titre } : null, dureeMs: e.dureeMs, depuis: e.depuis,
    pause: e.pause, pauseDepuis: e.pauseDepuis, enAttente: e.enAttente, horsLigne: e.horsLigne,
  };
}

/** Phases diffusées : l'onglet ne diffuse que pendant l'enregistrement et l'envoi de ses dernières parties. */
const PHASES: readonly PhaseScribe[] = ['enregistrement', 'envoi'];
const nombre = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const instant = (v: unknown): v is number | null => v === null || nombre(v);

/** État reçu d'un autre onglet (contrôlé : un message mal formé est ignoré). */
function lireEtat(x: unknown): EtatScribe | null {
  if (!x || typeof x !== 'object') return null;
  const e = x as Record<string, unknown>;
  const r = e.reunion as Record<string, unknown> | null | undefined;
  if (!PHASES.includes(e.phase as PhaseScribe) || !r || typeof r.id !== 'string' || typeof r.titre !== 'string') return null;
  if (!nombre(e.dureeMs) || !instant(e.depuis) || !instant(e.pauseDepuis) || !nombre(e.enAttente)) return null;
  return {
    ...ETAT_INITIAL, phase: e.phase as PhaseScribe, reunion: { id: r.id, titre: r.titre }, dureeMs: e.dureeMs, depuis: e.depuis,
    pause: e.pause === true, pauseDepuis: e.pauseDepuis, enAttente: e.enAttente, horsLigne: e.horsLigne === true,
  };
}

/** Message reçu sur le canal, ou null s'il est inconnu ou mal formé. */
export function lireMessage(x: unknown): { type: 'etat'; etat: EtatScribe } | Exclude<MessageOnglets, { type: 'etat' }> | null {
  if (!x || typeof x !== 'object') return null;
  const m = x as Record<string, unknown>;
  if (m.type === 'fin' || m.type === 'demande') return { type: m.type };
  if (m.type === 'arreter') return typeof m.reunion === 'string' ? { type: 'arreter', reunion: m.reunion } : null;
  if (m.type === 'etat') {
    const etat = lireEtat(m.etat);
    return etat ? { type: 'etat', etat } : null;
  }
  return null;
}

/** Canal ouvert, ou null (navigateur sans BroadcastChannel). */
export function ouvrirCanal(): BroadcastChannel | null {
  try {
    return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CANAL_SCRIBE);
  } catch {
    return null;
  }
}
